import type { SkiV3Extent } from "@/lib/conditions/ski-v3-carriers";
import {
  skiClassMarker,
  skiClassWord,
  skiExtentCompact,
  skiExtentNote,
  skiScoreTooltip,
  type SkiDisplayV3Fields,
} from "./ski-board-display";

/**
 * Class marker beside a v3 ski score: "Partly checked" for class B, "Estimated" for class C,
 * nothing for class A (checked against reports). The tooltip carries the class words and the
 * likely range ("Great. Partly checked. Likely 62 to 78."). Screen readers get the full class
 * words ("Estimated from weather models"); the range is already in the score's own hidden
 * text or shown beside it. Renders nothing for revision 2 rows, which keep ScoreConfidenceBadge.
 */
export function SkiClassChip({
  fields,
  label,
}: {
  fields: SkiDisplayV3Fields;
  label: string | null;
}) {
  const marker = skiClassMarker(fields.cls);
  if (!marker) return null;
  const tooltip = skiScoreTooltip(label, fields) ?? marker;
  return (
    <span
      title={tooltip}
      data-ski-class={fields.cls ?? undefined}
      className={`inline-flex min-h-6 items-center rounded-md border px-2 py-1 text-[10px] font-semibold leading-[1.25] ${
        fields.cls === "B"
          ? "border-amber-300 bg-amber-50 text-amber-900"
          : "border-dashed border-slate-400 bg-slate-50 text-slate-700"
      }`}
    >
      <span aria-hidden="true">{marker}</span>
      <span className="sr-only">{skiClassWord(fields.cls) ?? marker}</span>
    </span>
  );
}

/**
 * Compact open-terrain note for a v3 piste score ("40 % open"), with the full note as the
 * tooltip and for screen readers ("Limited terrain: 40 % open (55 km)"). Nothing when the
 * whole area is open.
 */
export function SkiExtentChip({ extent }: { extent: SkiV3Extent | null | undefined }) {
  const compact = skiExtentCompact(extent);
  if (!compact) return null;
  const note = skiExtentNote(extent) ?? compact;
  return (
    <span
      title={note}
      className="inline-flex min-h-6 items-center whitespace-nowrap rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] font-semibold leading-[1.25] text-sky-800"
    >
      <span aria-hidden="true">{compact}</span>
      <span className="sr-only">{note}</span>
    </span>
  );
}
